/*
  Persists collapsed/expanded state of grouped cart product cards across
  cart re-renders and page loads. Owned by this repo. See CUSTOMIZATIONS.md.
*/
(function () {
  const STORAGE_KEY = 'cart-grouped:collapsed';

  function readCollapsed() {
    try {
      return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'));
    } catch (e) {
      return new Set();
    }
  }

  function writeCollapsed(set) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
    } catch (e) {
      /* storage unavailable — ignore */
    }
  }

  function hydrate(root) {
    const collapsed = readCollapsed();
    root.querySelectorAll('details[data-product-id]').forEach((details) => {
      const id = details.getAttribute('data-product-id');
      if (collapsed.has(id)) {
        details.removeAttribute('open');
      } else {
        details.setAttribute('open', '');
      }
    });
  }

  function attach(root) {
    root.querySelectorAll('details[data-product-id]').forEach((details) => {
      if (details.dataset.cartGroupedBound === 'true') return;
      details.dataset.cartGroupedBound = 'true';
      details.addEventListener('toggle', () => {
        const id = details.getAttribute('data-product-id');
        const collapsed = readCollapsed();
        if (details.open) collapsed.delete(id);
        else collapsed.add(id);
        writeCollapsed(collapsed);
      });
    });
  }

  function init() {
    const root = document.getElementById('main-cart-items');
    if (!root) return;
    hydrate(root);
    attach(root);

    const target = root.querySelector('.js-contents') || root;
    const observer = new MutationObserver(() => {
      hydrate(root);
      attach(root);
    });
    observer.observe(target, { childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/*
  <cart-grouped-remove> — header trigger that batch-removes every variant of
  one product in a single /cart/update.js call. Confirms via the shared
  <dialog id="CartGroupedConfirm"> rendered once per cart page.
*/
(function () {
  const SECTION_TARGETS = [
    { id: 'main-cart-items', selector: '.js-contents', useDataId: true },
    { id: 'cart-icon-bubble', selector: '.shopify-section', section: 'cart-icon-bubble' },
    { id: 'cart-live-region-text', selector: '.shopify-section', section: 'cart-live-region-text' },
    { id: 'main-cart-footer', selector: '.js-contents', useDataId: true },
  ];

  function getSectionsToRender() {
    return SECTION_TARGETS
      .map((target) => {
        const node = document.getElementById(target.id);
        if (!node) return null;
        const sectionId = target.useDataId ? node.dataset.id : target.section;
        if (!sectionId) return null;
        return { ...target, node, section: sectionId };
      })
      .filter(Boolean);
  }

  function refreshSections(state) {
    const cartItems = document.querySelector('cart-items');
    const cartFooter = document.getElementById('main-cart-footer');
    const isEmpty = state.item_count === 0;
    if (cartItems) cartItems.classList.toggle('is-empty', isEmpty);
    if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);

    getSectionsToRender().forEach((target) => {
      const sectionHTML = state.sections && state.sections[target.section];
      if (!sectionHTML) return;
      const inner = target.node.querySelector(target.selector) || target.node;
      const parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const replacement = parsed.querySelector(target.selector);
      if (!replacement) return;
      inner.innerHTML = replacement.innerHTML;
    });
  }

  class CartGroupedRemove extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('button');
      if (this.button) {
        this.button.addEventListener('click', this.onTriggerClick.bind(this));
      }
    }

    onTriggerClick(event) {
      // Stop the click from bubbling to <summary> and toggling <details>.
      event.preventDefault();
      event.stopPropagation();

      const dialog = document.getElementById('CartGroupedConfirm');
      if (!dialog || typeof dialog.showModal !== 'function') {
        // Browser without <dialog> support — fall back to native confirm.
        if (window.confirm(`Remove all ${this.dataset.productTitle} from your cart?`)) {
          this.submit();
        }
        return;
      }

      this.populate(dialog);
      this.bindDialog(dialog);
      dialog.showModal();

      // Focus the cancel button by default — confirm action is destructive.
      const cancelBtn = dialog.querySelector('[data-cart-grouped-confirm="cancel"]:not(.cart-grouped-confirm__close)');
      if (cancelBtn) cancelBtn.focus();
    }

    populate(dialog) {
      const titleEl = dialog.querySelector('[data-cart-grouped-confirm="title"]');
      const metaEl = dialog.querySelector('[data-cart-grouped-confirm="meta"]');
      const totalQty = parseInt(this.dataset.totalQty, 10) || 0;
      const variantCount = parseInt(this.dataset.variantCount, 10) || 0;
      const itemWord = totalQty === 1 ? 'item' : 'items';
      const variantWord = variantCount === 1 ? 'size' : 'sizes';

      if (titleEl) titleEl.textContent = this.dataset.productTitle || '';
      if (metaEl) {
        metaEl.textContent =
          variantCount > 0
            ? `${totalQty} ${itemWord} across ${variantCount} ${variantWord} will be removed from your cart.`
            : `${totalQty} ${itemWord} will be removed from your cart.`;
      }
    }

    bindDialog(dialog) {
      const confirmBtn = dialog.querySelector('[data-cart-grouped-confirm="confirm"]');
      const cancelBtns = dialog.querySelectorAll('[data-cart-grouped-confirm="cancel"]');

      // Replace listeners by cloning — keeps each open scoped to one trigger.
      const freshConfirm = confirmBtn.cloneNode(true);
      confirmBtn.replaceWith(freshConfirm);
      freshConfirm.addEventListener('click', () => {
        dialog.close('confirm');
        this.submit();
      });

      cancelBtns.forEach((btn) => {
        const fresh = btn.cloneNode(true);
        btn.replaceWith(fresh);
        fresh.addEventListener('click', () => dialog.close('cancel'));
      });

      // Click outside the inner panel closes the dialog.
      if (!dialog.dataset.backdropBound) {
        dialog.dataset.backdropBound = 'true';
        dialog.addEventListener('click', (e) => {
          if (e.target === dialog) dialog.close('cancel');
        });
      }
    }

    async submit() {
      const cartItems = document.querySelector('cart-items');
      if (!cartItems) return;

      const variantIds = (this.dataset.variantIds || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
      if (variantIds.length === 0) return;

      const updates = variantIds.reduce((acc, id) => {
        acc[id] = 0;
        return acc;
      }, {});

      cartItems.classList.add('cart__items--disabled');

      const sections = getSectionsToRender().map((target) => target.section);
      const body = JSON.stringify({
        updates,
        sections,
        sections_url: window.location.pathname,
      });

      try {
        const response = await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body });
        const state = await response.json();
        refreshSections(state);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-grouped-remove', cartData: state });
        }
      } catch (err) {
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors && window.cartStrings && window.cartStrings.error) {
          errors.textContent = window.cartStrings.error;
        }
      } finally {
        cartItems.classList.remove('cart__items--disabled');
      }
    }
  }

  if (!customElements.get('cart-grouped-remove')) {
    customElements.define('cart-grouped-remove', CartGroupedRemove);
  }
})();
